import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CircleParking } from "lucide-react";
import { DashboardShell, PlaceholderCard, StatCard } from "@/components/DashboardShell";
import { ADMIN_NAV } from "@/lib/nav";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/parking")({
  head: () => ({
    meta: [
      { title: "Parking moderation · Landoo Admin" },
      {
        name: "description",
        content: "Review, approve and moderate parking listings across the platform.",
      },
    ],
  }),
  component: AdminParkingPage,
});

function AdminParkingPage() {
  const { data = [] } = useQuery({
    queryKey: ["admin", "parking"],
    queryFn: async () => {
      const { data } = await supabase
        .from("parking_listings")
        .select("id,title,city,status,total_slots,available_slots,created_at")
        .order("created_at", { ascending: false })
        .limit(50);
      return (
        (data as {
          id: string;
          title: string;
          city: string | null;
          status: string;
          total_slots: number;
          available_slots: number;
        }[]) ?? []
      );
    },
  });

  const pending = data.filter((p) => p.status === "pending").length;
  const published = data.filter((p) => p.status === "published").length;

  return (
    <DashboardShell title="Parking moderation" role="Admin" items={ADMIN_NAV}>
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Parking listings" value={data.length} icon={CircleParking} />
        <StatCard
          label="Pending approval"
          value={pending}
          hint="Awaiting review"
          icon={CircleParking}
        />
        <StatCard label="Published" value={published} hint="Live on the map" icon={CircleParking} />
      </div>

      {data.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Parking</th>
                <th className="px-3 py-2">City</th>
                <th className="px-3 py-2">Slots</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((p) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="px-3 py-2 font-semibold text-brown-dark">{p.title}</td>
                  <td className="px-3 py-2 text-xs">{p.city ?? "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    {p.available_slots}/{p.total_slots}
                  </td>
                  <td className="px-3 py-2 text-xs capitalize">{p.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <PlaceholderCard
          title="Parking approval queue"
          description="Approve or reject submitted parking listings."
        />
        <PlaceholderCard
          title="Parking complaints"
          description="Complaints raised against parking providers."
        />
        <PlaceholderCard
          title="Parking provider verification"
          description="Verify parking operator documents and ownership."
        />
        <PlaceholderCard
          title="Booking oversight"
          description="Monitor parking booking requests once bookings go live."
        />
      </div>
    </DashboardShell>
  );
}
